/*********************** Custom JS for Boost AI Search & Discovery (Turbo) ************************/
/**
 * SeamsFriendly - Boost Turbo Customization
 *
 * Customizes:
 *   - Product card rendering (images, labels, stickers, swatches, price, reviews)
 *   - "Customizable" badge for eligible products
 *   - LOOX review integration
 *   - Tall/short aspect ratio classes from product tags
 *   - Color swatch rendering with variant image switching
 *   - Custom filter mobile toolbar
 *   - Scroll-to-top on filter/sort
 *   - "In the mood to browse?" end-of-list CTA
 */
(function () {
  'use strict';

  /* ──────────────────────────────────────────────
     CONFIGURATION
     ────────────────────────────────────────────── */
  var SF_CONFIG = {
    productsPerPage: 24,
    mobileItemsPerRow: 2,
    tabletItemsPerRow: 3,
    desktopItemsPerRow: 4,
    showVendor: false,
    showColorSwatch: true,
    showLabels: true,
    showProductInfo: true,
    showSecondaryImage: true,
    productImageSize: 'natural',
    productInfoAlignment: 'left',
    swatchExtension: 'png',
    noImageUrl: '',
    labels: {
      sale: 'On Sale',
      soldOut: 'Sold out',
      fromPrice: 'From',
      viewProduct: 'View'
    }
  };

  /* ──────────────────────────────────────────────
     UTILITY HELPERS
     ────────────────────────────────────────────── */
  function slugify(text) {
    if (!text) return '';
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = (parseInt(cents, 10) / 100).toFixed(2);
    // Use Shopify global money format if available
    var moneyFormat = (window.Shopify && window.Shopify.money_format) ||
                      (window.theme && window.theme.moneyFormat) ||
                      '${{amount}}';
    return moneyFormat
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', parseInt(value, 10))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', parseInt(value, 10));
  }

  function optimizeImage(src, size) {
    if (!src) return '';
    if (size) {
      var match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
      if (match) {
        var prefix = src.split(match[0])[0];
        return prefix + '_' + size + '.' + match[1] + (match[2] || '');
      }
    }
    return src;
  }

  function imageSize(sizes, image) {
    if (!image || !image.width) return sizes;
    var desired = sizes.split(',');
    var supported = '';
    for (var k = 0; k < desired.length; k++) {
      if (image.width < parseInt(desired[k], 10)) break;
      supported += desired[k] + ',';
    }
    if (!supported) return String(image.width);
    return supported.slice(0, -1);
  }

  function buildSrcset(image) {
    var sizes = [200, 400, 600, 700, 800, 900, 1000, 1200];
    var srcset = '';
    for (var i = 0; i < sizes.length; i++) {
      if (image.width < sizes[i]) break;
      if (srcset) srcset += ', ';
      srcset += optimizeImage(image.src, sizes[i] + 'x') + ' ' + sizes[i] + 'w';
    }
    if (!srcset) {
      srcset += optimizeImage(image.src, image.width + 'x') + ' ' + image.width + 'w';
    }
    return srcset;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function buildProductUrl(product) {
    return '/products/' + product.handle;
  }

  /* ──────────────────────────────────────────────
     PRODUCT CARD BUILDER
     ────────────────────────────────────────────── */

  function buildProductCard(product, index) {
    var data = prepareProductData(product);
    var html = '';

    html += '<div class="boost-sd-product-card sf-product-card" data-product-id="' + product.id + '">';
    html += '<div class="sf-product-card__wrapper">';
    html += buildProductImage(data);
    html += buildProductLabels(data);
    html += buildProductInfo(data, index);
    html += '</div>';
    html += '</div>';

    return html;
  }

  function prepareProductData(data) {
    data._soldOut = !data.available;
    data._onSale = data.compare_at_price_min > data.price_min;
    data._priceVaries = data.price_min !== data.price_max;
    data._images = data.images_info || data.images || [];
    data._firstVariant = data.variants ? data.variants[0] : {};

    // Find first available variant
    if (data.variants) {
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].available) {
          data._firstVariant = data.variants[i];
          break;
        }
      }
    }

    return data;
  }

  /* ── Image Builder ── */
  function buildProductImage(data) {
    var images = data._images || [];
    var html = '';

    if (images.length === 0) {
      images.push({
        src: SF_CONFIG.noImageUrl || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
        id: data.id,
        width: 480,
        height: 480
      });
    }

    var productUrl = buildProductUrl(data);
    var hasAlternate = SF_CONFIG.showSecondaryImage && images.length > 1;

    // Determine aspect ratio class from tags
    var sizeClass = '';
    if (data.tags) {
      for (var i = 0; i < data.tags.length; i++) {
        if (data.tags[i].indexOf('Lower Size') !== -1 || data.tags[i].indexOf('Coord_Size') !== -1) {
          sizeClass = 'losz';
          break;
        } else if (data.tags[i].indexOf('Upper Size') !== -1) {
          sizeClass = 'upsz';
          break;
        }
      }
    }

    html += '<a href="' + productUrl + '" class="sf-product-card__image-wrapper ProductItem__ImageWrapper';
    if (hasAlternate) html += ' ProductItem__ImageWrapper--withAlternateImage';
    if (sizeClass) html += ' ' + sizeClass;
    html += '">';

    var aspectRatio = images[0].width / images[0].height;
    html += '<div class="AspectRatio AspectRatio--withFallback" style="max-width: ' + images[0].width + 'px; padding-bottom: ' + (100 / aspectRatio) + '%; --aspect-ratio: ' + aspectRatio + '">';

    // Alternate image (shown on hover)
    if (hasAlternate) {
      var altSrcset = buildSrcset(images[1]);
      var altFallback = optimizeImage(images[1].src, '600x');
      html += '<img class="ProductItem__Image ProductItem__Image--alternate" src="' + altFallback + '" srcset="' + altSrcset + '" sizes="(max-width: 749px) 50vw, 25vw" alt="' + escapeHtml(data.title) + '" loading="lazy" data-image-id="' + images[1].id + '">';
    }

    // Primary image
    var mainSrcset = buildSrcset(images[0]);
    var mainFallback = optimizeImage(images[0].src, '600x');
    html += '<img class="ProductItem__Image" src="' + mainFallback + '" srcset="' + mainSrcset + '" sizes="(max-width: 749px) 50vw, 25vw" alt="' + escapeHtml(data.title) + '" loading="lazy" data-image-id="' + images[0].id + '">';

    // Noscript fallback
    html += '<noscript><img class="ProductItem__Image" src="' + mainFallback + '" alt="' + escapeHtml(data.title) + '"></noscript>';

    html += '</div>';
    html += '</a>';

    return html;
  }

  /* ── Labels Builder ── */
  function buildProductLabels(data) {
    var html = '';
    var labels = '';

    if (!SF_CONFIG.showLabels) return '';

    // Check for __label tags
    if (data.tags) {
      for (var k = 0; k < data.tags.length; k++) {
        if (data.tags[k].indexOf('__label') !== -1) {
          labels += '<span class="ProductItem__Label Heading Text--subdued">' + data.tags[k].split('__label:')[1] + '</span>';
          break;
        }
      }
    }

    // Sale / Sold out labels
    if (!data._soldOut) {
      if (data._onSale) {
        labels += '<span class="ProductItem__Label Heading Text--subdued">' + SF_CONFIG.labels.sale + '</span>';
      }
    } else {
      labels += '<span class="ProductItem__Label Heading Text--subdued">' + SF_CONFIG.labels.soldOut + '</span>';
    }

    if (labels) {
      html += '<div class="ProductItem__LabelList">' + labels + '</div>';
    }

    // Metafield-based PLP sticker
    var metaSticker = getProductMetafield(data, 'custom', 'plp_sticker');
    if (metaSticker) {
      html += '<div class="label_icon label_icon-mob ' + slugify(metaSticker) + '">' + metaSticker + '</div>';
    }

    return html;
  }

  /* ── Product Info Builder ── */
  function buildProductInfo(data, index) {
    var html = '';
    if (!SF_CONFIG.showProductInfo) return '';

    html += '<div class="ProductItem__Info ProductItem__Info--' + SF_CONFIG.productInfoAlignment + '">';

    // Color swatches above title
    if (SF_CONFIG.showColorSwatch) {
      html += buildSwatch(data, index);
    }

    if (SF_CONFIG.showVendor) {
      html += '<p class="ProductItem__Vendor Heading">' + (data.vendor || '') + '</p>';
    }

    html += '<div class="info-title">';
    html += '<h2 class="ProductItem__Title Heading">';
    html += '<a href="' + buildProductUrl(data) + '">' + data.title + '</a>';


    html += '</h2>';

    html += '<div class="boost-sd-custom-container">';
    html += buildPrice(data);

    // Customizable badge
    var isCustomizable = true;
    if (data.tags) {
      if (data.tags.indexOf('Non-Customizable') !== -1 || data.tags.indexOf('SMPLS') !== -1) {
        isCustomizable = false;
      }
    }
    if (isCustomizable) {
      html += '<p class="cust modal-toggle" id="mybBtn">\uD83E\uDEA1 Customizable</p>';
    }

    html += '</div></div>';
    html += '</div>';

    return html;
  }

  /* ── Price Builder ── */
  function buildPrice(data) {
    var html = '<div class="ProductItem__PriceList Heading">';

    if (data._onSale) {
      html += '<span class="ProductItem__Price Price Price--highlight Text--subdued" data-money-convertible>' + formatMoney(data.price_min) + '</span> ';
      html += '<span class="ProductItem__Price Price Price--compareAt Text--subdued" data-money-convertible>' + formatMoney(data.compare_at_price_min) + '</span>';
    } else {
      if (data._priceVaries) {
        html += '<span class="ProductItem__Price Price Text--subdued">' + formatMoney(data.price_min) + '</span>';
      } else {
        html += '<span class="ProductItem__Price Price Text--subdued" data-money-convertible>' + formatMoney(data.price_min) + '</span>';
      }
    }

    html += '</div>';
    return html;
  }


  /* ── Swatch Builder ── */
  function buildSwatch(data, index) {
    var html = '';
    if (!data.options_with_values || !data.variants) return '';

    data.options_with_values.forEach(function (option) {
      var optionName = option.name.toLowerCase();
      if (optionName.indexOf('color') === -1 && optionName.indexOf('colour') === -1 && optionName.indexOf('couleur') === -1) return;

      var values = '';
      var colorName = 'boost-sd-' + data.id + '-' + index;
      html += '<div class="ProductItem__ColorSwatchList">';
      var i = 0;

      data.variants.forEach(function (variant) {
        var temp = variant.merged_options ? variant.merged_options.filter(function (obj) {
          obj = obj.toLowerCase();
          return obj.indexOf('color') !== -1 || obj.indexOf('colour') !== -1;
        }) : [];

        if (temp.length === 0) return;
        var parts = temp[0].split(':');
        var value = parts[1] ? parts[1].toLowerCase() : '';

        if (values.indexOf(value) !== -1) return;
        values += ',' + value;

        var variantImage = variant.image || (data._images && data._images.length > 0 ? data._images[0].src : '');
        var imageInfo = null;
        var imageAspect = 1;

        if (data._images) {
          imageInfo = data._images.find(function (img) {
            if (img.src === variant.image) {
              imageAspect = img.width / img.height;
              return true;
            }
            return false;
          });
        }

        if (!imageInfo && data._images && data._images.length > 0) {
          imageInfo = data._images[0];
        }

        var supportedSizes = imageInfo ? imageSize('200,400,600,700,800,900,1000,1200', imageInfo) : '';
        var checked = i === 0 ? 'checked="checked"' : '';
        var inputId = colorName + '-' + i;
        var variantPrice = variant.price || 0;
        var variantComparePrice = variant.compare_at_price || 0;
        var swatchUrl = '/files/' + slugify(value) + '.' + SF_CONFIG.swatchExtension;

        html += '<div class="ProductItem__ColorSwatchItem">';
        html += '<input class="ColorSwatch__Radio" type="radio" ' + checked + ' name="' + colorName + '" id="' + inputId + '" value="' + value + '"';
        html += ' data-image-aspect-ratio="' + imageAspect + '"';
        html += ' data-variant-price="' + variantPrice + '"';
        html += ' data-variant-compare-at-price="' + variantComparePrice + '"';
        html += ' data-variant-url="' + buildProductUrl(data) + '?variant=' + variant.id + (imageInfo ? '#Image' + imageInfo.id : '') + '"';
        if (imageInfo) {
          html += ' data-image-url="' + imageInfo.src + '"';
          html += ' data-image-id="' + imageInfo.id + '"';
        }
        html += ' aria-hidden="true">';
        html += '<label class="ColorSwatch ColorSwatch--small" for="' + inputId + '" style="background-color: ' + value.replace(/\s/g, '').toLowerCase() + '; background-image: url(' + swatchUrl + ')" title="' + value + '" data-tooltip="' + value + '"></label>';
        html += '</div>';
        i++;
      });

      html += '</div>';
    });

    return html;
  }

  /* ── Metafield Helper ── */
  function getProductMetafield(data, namespace, key) {
    if (!data.metafields) return null;
    for (var i = 0; i < data.metafields.length; i++) {
      var mf = data.metafields[i];
      if (mf.namespace === namespace && mf.key === key) {
        return mf.value;
      }
    }
    return null;
  }

  /* ──────────────────────────────────────────────
     SWATCH INTERACTION (after render)
     ────────────────────────────────────────────── */
  function initSwatchInteractions() {
    document.querySelectorAll('.sf-product-card .ProductItem__ColorSwatchItem label.ColorSwatch').forEach(function (label) {
      label.addEventListener('click', function () {
        var parent = label.closest('.ProductItem__ColorSwatchItem');
        var swatchList = label.closest('.ProductItem__ColorSwatchList');
        var card = label.closest('.sf-product-card__wrapper');

        // Active state
        swatchList.querySelectorAll('label.ColorSwatch').forEach(function (l) { l.classList.remove('active'); });
        label.classList.add('active');

        // Swap image
        var radio = parent.querySelector('input.ColorSwatch__Radio');
        if (radio && radio.dataset.imageUrl) {
          var imageWrapper = card.querySelector('.sf-product-card__image-wrapper');
          if (imageWrapper) {
            var existingSwap = imageWrapper.querySelector('.bc-sf-product-swatch-img');
            if (existingSwap) existingSwap.remove();

            var aspect = imageWrapper.querySelector('.AspectRatio');
            if (aspect) {
              var img = document.createElement('img');
              img.className = 'bc-sf-product-swatch-img';
              img.src = radio.dataset.imageUrl;
              img.alt = radio.value;
              aspect.prepend(img);
              aspect.querySelectorAll('img.ProductItem__Image').forEach(function (pi) { pi.style.display = 'none'; });
            }
            imageWrapper.setAttribute('href', radio.dataset.variantUrl || imageWrapper.getAttribute('href'));
          }
        }

        // Update price
        if (radio) {
          var priceList = card.querySelector('.ProductItem__PriceList');
          if (priceList) {
            var variantPrice = parseInt(radio.dataset.variantPrice, 10) || 0;
            var variantCompare = parseInt(radio.dataset.variantCompareAtPrice, 10) || 0;
            var priceHtml = '';
            if (variantCompare > variantPrice) {
              priceHtml += '<span class="ProductItem__Price Price Price--highlight Text--subdued" data-money-convertible>' + formatMoney(variantPrice) + '</span>';
              priceHtml += '<span class="ProductItem__Price Price Price--compareAt Text--subdued" data-money-convertible>' + formatMoney(variantCompare) + '</span>';
            } else {
              priceHtml += '<span class="ProductItem__Price Price Text--subdued" data-money-convertible>' + formatMoney(variantPrice) + '</span>';
            }
            priceList.innerHTML = priceHtml;
          }
        }
      });
    });
  }

  /* ──────────────────────────────────────────────
     CUSTOMIZABLE MODAL TOGGLE
     ────────────────────────────────────────────── */
  function initCustomizableToggle() {
    document.querySelectorAll('.modal-toggle').forEach(function (btn) {
      if (btn.dataset.sfCustBound) return;
      btn.dataset.sfCustBound = 'true';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var modal = document.querySelector('.modalin');
        if (modal) {
          modal.classList.toggle('is-visible');
          document.getElementById('jsscri').style.overflow = modal.classList.contains('is-visible') ? 'hidden' : 'auto';
        }
      });
    });
  }

  /* ──────────────────────────────────────────────
     SCROLL TO TOP ON FILTER/SORT
     ────────────────────────────────────────────── */
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ──────────────────────────────────────────────
     MOBILE HELPERS
     ────────────────────────────────────────────── */
  function isMobile() {
    return window.innerWidth < 768;
  }

  function isSearchPage() {
    return window.location.pathname.indexOf('/search') !== -1;
  }

  /* ──────────────────────────────────────────────
     PRODUCT COUNT & PROGRESS BAR (from v2 FilterResult.afterRender)
     ────────────────────────────────────────────── */

  // Persistent store for the total product count across re-renders
  var _sfTotalProductCount = 0;

  /**
   * Inject or update the custom product count element.
   * Tries multiple insertion points to guarantee visibility.
   */
  function injectCustomProductCount(countText) {
    var existing = document.getElementById('sf-custom-product-count');
    if (existing) {
      // Already exists and text matches — nothing to do
      if (existing.textContent === countText) {
        injectDesktopProductCount(countText);
        return;
      }
      existing.remove();
    }

    var el = document.createElement('div');
    el.id = 'sf-custom-product-count';
    el.textContent = countText;

    // Primary: insert before .boost-sd__product-list
    var productList = document.querySelector('.boost-sd__product-list');
    if (productList && productList.parentNode) {
      productList.parentNode.insertBefore(el, productList);
      injectDesktopProductCount(countText);
      return;
    }

    // Fallback: insert after toolbar
    var toolbar = document.querySelector('.boost-sd__toolbar') ||
                  document.querySelector('.boost-sd__collection-toolbar');
    if (toolbar && toolbar.parentNode) {
      toolbar.parentNode.insertBefore(el, toolbar.nextSibling);
      injectDesktopProductCount(countText);
      return;
    }

    // Last resort: append to Boost layout container
    var layout = document.querySelector('.boost-sd-layout');
    if (layout) {
      layout.appendChild(el);
    }
    injectDesktopProductCount(countText);
  }

  /**
   * Inject or update the desktop product count element next to (left of) the sort dropdown.
   * Shows "Showing X of Y products" format with a vertical divider (via CSS border-right).
   */
  function injectDesktopProductCount(countText) {
    // Only inject on desktop
    if (window.innerWidth < 750) return;

    var productsShown = document.querySelectorAll('.boost-sd__product-item').length;
    var total = _sfTotalProductCount;
    var displayText = (productsShown && total)
      ? 'Showing ' + productsShown + ' of ' + total + ' products'
      : countText;

    var existing = document.getElementById('sf-desktop-product-count');
    if (existing) {
      existing.textContent = displayText;
      return;
    }

    // Strategy 1: Find sorting inside .boost-sd__toolbar
    var toolbar = document.querySelector('.boost-sd__toolbar');
    if (!toolbar) toolbar = document.querySelector('.boost-sd__collection-toolbar');

    // Strategy 2: Find any .boost-sd__sorting on the page (outside mobile toolbar)
    var sortEl = null;
    if (toolbar) {
      sortEl = toolbar.querySelector('.boost-sd__sorting');
    }
    if (!sortEl) {
      // Try all sorting elements, skip the one inside mobile toolbar
      var allSortEls = document.querySelectorAll('.boost-sd__sorting');
      for (var i = 0; i < allSortEls.length; i++) {
        if (!allSortEls[i].closest('.boost-sd__toolbar-top-mobile')) {
          sortEl = allSortEls[i];
          break;
        }
      }
    }
    if (!sortEl) return;

    var el = document.createElement('span');
    el.id = 'sf-desktop-product-count';
    el.textContent = displayText;

    // Walk up from sortEl to find the best insertion point
    // Insert as a sibling right before the sort element (or its toolbar-item wrapper)
    var insertBefore = sortEl;
    var parent = sortEl.parentElement;
    while (parent && parent !== toolbar && parent.classList && !parent.classList.contains('boost-sd__toolbar')) {
      insertBefore = parent;
      parent = parent.parentElement;
    }

    if (parent) {
      parent.insertBefore(el, insertBefore);
    } else {
      // Fallback: insert right before the sort element itself
      sortEl.parentNode.insertBefore(el, sortEl);
    }
  }

  /**
   * Build count text from any available source.
   */
  function getProductCountText(data) {
    var total = 0;

    // Source 1: event data from Boost
    if (data && data.total_product) {
      total = data.total_product;
    }

    // Source 2: previously stored total
    if (!total && _sfTotalProductCount > 0) {
      total = _sfTotalProductCount;
    }

    // Source 3: native Boost DOM (hidden via CSS but text may exist)
    if (!total) {
      var nativeEls = [
        document.querySelector('.boost-sd__filter-total-product'),
        document.querySelector('.boost-sd__product-count'),
        document.querySelector('.boost-sd__toolbar-item--product-count')
      ];
      for (var i = 0; i < nativeEls.length; i++) {
        if (nativeEls[i] && nativeEls[i].textContent.trim()) {
          var match = nativeEls[i].textContent.match(/(\d[\d,]*)/);
          if (match) {
            total = parseInt(match[1].replace(/,/g, ''), 10);
            break;
          }
        }
      }
    }

    // Source 4: count visible DOM items
    if (!total) {
      var items = document.querySelectorAll('.boost-sd__product-item');
      if (items.length > 0) total = items.length;
    }

    if (!total) return null;

    _sfTotalProductCount = total;

    if (isSearchPage()) {
      var q = getSearchQuery();
      return total === 1
        ? total + " result for '" + q + "'"
        : total + " results for '" + q + "'";
    }
    return total !== 1 ? total + ' products' : total + ' product';
  }

  function updateProductCount(data) {
    // Top counter: always show total count text
    var countText = getProductCountText(data);
    if (countText) {
      injectCustomProductCount(countText);
    }

    // Bottom counter above Load More: "Showing X of Y products"
    var productsShown = document.querySelectorAll('.boost-sd__product-item').length;
    var totalProduct = _sfTotalProductCount;

    // Fallback: try reading total from Boost's load-more-total or data attribute
    if (!totalProduct) {
      var totalEl = document.querySelector('.boost-sd__load-more-total');
      if (totalEl && totalEl.textContent.trim()) {
        var m = totalEl.textContent.match(/(\d[\d,]*)/);
        if (m) totalProduct = parseInt(m[1].replace(/,/g, ''), 10);
      }
    }
    if (!totalProduct) {
      var loadMoreBtn = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');
      if (loadMoreBtn) {
        var btnText = loadMoreBtn.textContent || '';
        var m2 = btnText.match(/(\d[\d,]*)/);
        if (m2) totalProduct = parseInt(m2[1].replace(/,/g, ''), 10);
      }
    }

    if (totalProduct) _sfTotalProductCount = totalProduct;
    injectPageProgress(productsShown, totalProduct);
  }

  /**
   * Inject or update a "Showing X of Y products" counter above the Load More button.
   */
  function injectPageProgress(shown, total) {
    var hasLoadMore = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');

    if (!shown || !total || shown >= total || !hasLoadMore) {
      // Remove if all products are shown or no Load More button
      var existing = document.getElementById('sf-custom-page-progress');
      if (existing) existing.remove();
      return;
    }

    var text = 'Showing ' + shown + ' of ' + total + ' products';
    var el = document.getElementById('sf-custom-page-progress');

    if (el) {
      el.textContent = text;
      return;
    }

    // Find the Load More button container to insert before it
    var loadMoreWrapper = document.querySelector('.boost-sd__pagination-load-more-container-item') ||
                          document.querySelector('.boost-sd__load-more-wrapper') ||
                          document.querySelector('.boost-sd__pagination');
    if (!loadMoreWrapper) {
      // Last resort: use the Load More button's parent as the anchor
      var btn = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');
      if (btn) loadMoreWrapper = btn.parentElement || btn;
    }
    if (loadMoreWrapper) {
      el = document.createElement('div');
      el.id = 'sf-custom-page-progress';
      el.textContent = text;
      // Inline styles — CSS !important loses to Boost's dynamically loaded CSS
      el.style.cssText = 'text-align:center;width:100%;padding:8px 0 14px;color:#737373;font-weight:400;font-size:13px;letter-spacing:0.5px;text-transform:uppercase;';
      loadMoreWrapper.parentNode.insertBefore(el, loadMoreWrapper);
    }
  }

  /* ──────────────────────────────────────────────
     LOAD MORE BUTTON — INLINE STYLE (Boost CSS overrides theme CSS,
     so we apply styles via JS after Boost renders the button)
     ────────────────────────────────────────────── */
  function styleLoadMoreButton() {
    var btn = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');
    if (!btn || btn.dataset.sfStyled) return;
    btn.style.cssText = 'display:inline-block;background-color:#29705a;color:#fff;border:none;border-radius:2rem;padding:14px 40px;font-size:15px;font-weight:600;cursor:pointer;text-transform:none;letter-spacing:0.3px;min-width:200px;transition:background-color 0.2s ease,opacity 0.2s ease;';
    btn.dataset.sfStyled = '1';

    // Hover effect via event listeners (can't do :hover with inline styles)
    btn.addEventListener('mouseenter', function() { btn.style.backgroundColor = '#1e5c47'; });
    btn.addEventListener('mouseleave', function() { btn.style.backgroundColor = '#29705a'; });

    // Center the parent wrapper
    var wrapper = btn.closest('.boost-sd__pagination-load-more-container-item') ||
                  btn.closest('.boost-sd__load-more-wrapper') ||
                  btn.closest('.boost-sd__pagination');
    if (wrapper) wrapper.style.textAlign = 'center';
  }


  /**
   * BULLETPROOF POLLER: Runs every second, ensures the product count is
   * always visible whenever products are on the page. Does not depend on
   * any Boost events. Stops after 60 seconds to avoid infinite polling.
   */
  (function startProductCountPoller() {
    var attempts = 0;
    var maxAttempts = 60;
    var pollId = setInterval(function() {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollId);
        return;
      }
      var hasProducts = document.querySelector('.boost-sd__product-item');
      if (hasProducts) {
        updateProductCount(null);
        styleLoadMoreButton();
        styleSwatchExpanders();
      }
    }, 1000);
  })();

  /**
   * Style the "+X" swatch count elements inside Boost's <variant-swatch-king>.
   * The component may use shadow DOM or light DOM — we handle both.
   * Matches original theme: 24px grey circle, 11px font.
   */
  function styleSwatchExpanders() {
    var swatchKings = document.querySelectorAll('variant-swatch-king');
    swatchKings.forEach(function(king) {
      // Check both light DOM and shadow DOM children
      var roots = [king];
      if (king.shadowRoot) roots.push(king.shadowRoot);

      roots.forEach(function(root) {
        // Find all child elements and text nodes that contain "+N" pattern
        var allEls = root.querySelectorAll('*');
        allEls.forEach(function(el) {
          if (el.dataset.sfSwatchStyled) return;
          var text = el.textContent.trim();
          // Match "+N" pattern (e.g., "+36", "+1", "+41")
          if (/^\+\d+$/.test(text) && el.children.length === 0) {
            el.dataset.sfSwatchStyled = 'true';
            el.style.display = 'inline-flex';
            el.style.alignItems = 'center';
            el.style.justifyContent = 'center';
            el.style.width = 'auto';
            el.style.height = 'auto';
            el.style.minWidth = '24px';
            el.style.minHeight = '24px';
            el.style.padding = '5px';
            el.style.border = '0';
            el.style.borderRadius = '50%';
            el.style.backgroundColor = '#f0f0f0';
            el.style.color = 'rgba(0,0,0,0.6)';
            el.style.fontSize = '10px';
            el.style.fontWeight = '400';
            el.style.lineHeight = '1';
            el.style.flexShrink = '0';
            el.style.boxSizing = 'border-box';
            el.style.aspectRatio = '1';
            el.style.marginLeft = '2px';
          }
        });
      });

      // Hide variant-swatch-king if it has no visible swatch content
      // (CSS :has() fallback for older browsers and shadow DOM cases)
      var hasSwatches = king.querySelector('.swatch-navigable-wrapper') ||
                        king.querySelector('[class*="swatch"]') ||
                        (king.shadowRoot && king.shadowRoot.querySelector('.swatch-navigable-wrapper'));
      if (!hasSwatches) {
        king.style.display = 'none';
        king.style.height = '0';
        king.style.padding = '0';
        king.style.margin = '0';
        king.style.overflow = 'hidden';
      }

      // Add spacing between image and product info when swatch area takes no layout space
      var productInfo = king.nextElementSibling;
      if (productInfo && productInfo.classList.contains('boost-sd__product-info-wrapper')) {
        if (king.offsetHeight < 5) {
          productInfo.style.marginTop = '8px';
        } else {
          productInfo.style.marginTop = '';
        }
      }
    });
  }

  function getSearchQuery() {
    var params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  }

  /* ──────────────────────────────────────────────
     "REMOVE FILTERS" MOBILE CLEAR BUTTON (pill style — matches original)
     ────────────────────────────────────────────── */
  function updateMobileClearButton() {
    // Find or create the mobile clear button container
    var clearBtn = document.querySelector('.boost-sd__filter-clear-mobile');
    if (!clearBtn) {
      // Insert right after the mobile sort/filter bar, or after the toolbar container
      var mobileBar = document.querySelector('.boost-sd__toolbar-top-mobile');
      var insertAfter = mobileBar ||
                        document.querySelector('.boost-sd__toolbar-container') ||
                        document.querySelector('.boost-sd__toolbar') ||
                        document.querySelector('.boost-sd__collection-toolbar');
      if (insertAfter) {
        clearBtn = document.createElement('div');
        clearBtn.className = 'boost-sd__filter-clear-mobile';
        insertAfter.parentNode.insertBefore(clearBtn, insertAfter.nextSibling);
      } else {
        return;
      }
    }

    // Check if any filters are active via URL params or Boost's active filter elements
    var params = new URLSearchParams(window.location.search);
    var hasFilters = false;
    params.forEach(function (value, key) {
      if (key !== 'sort_by' && key !== 'q' && key !== 'page' && key !== 'type' && key !== 'view') {
        hasFilters = true;
      }
    });

    // Also check for Boost Turbo active filter indicators in the DOM
    if (!hasFilters) {
      var activeFilters = document.querySelectorAll(
        '.boost-sd__filter-option-item-button--selected, ' +
        '.boost-sd__refine-by-item, ' +
        '[class*="boost-sd__filter-selected"]'
      );
      if (activeFilters.length > 0) {
        hasFilters = true;
      }
    }

    if (hasFilters) {
      // Only re-render if not already showing (avoid duplicate event listeners)
      if (!clearBtn.querySelector('.boost-sd__filter-clear-all')) {
        clearBtn.innerHTML = '<a href="javascript:;" aria-label="Clear All Filters" class="boost-sd__filter-clear-all">Remove Filters</a>';
        clearBtn.querySelector('.boost-sd__filter-clear-all').addEventListener('click', function () {
          // Try Boost Turbo's built-in clear-all button first
          var boostClearAll = document.querySelector(
            '.boost-sd__refine-by-clear-all a, ' +
            '.boost-sd__refine-by-clear-all button, ' +
            '[data-action*="resetAllFilters"]'
          );
          if (boostClearAll) {
            boostClearAll.click();
          } else {
            // Fallback: remove filter params from URL and reload
            var url = new URL(window.location.href);
            var keysToRemove = [];
            url.searchParams.forEach(function (v, k) {
              if (k !== 'sort_by' && k !== 'q' && k !== 'page' && k !== 'type' && k !== 'view') {
                keysToRemove.push(k);
              }
            });
            keysToRemove.forEach(function (k) { url.searchParams.delete(k); });
            window.location.href = url.toString();
          }
          scrollToTop();
        });
      }
    } else {
      clearBtn.innerHTML = '';
    }
  }

  /* ──────────────────────────────────────────────
     "IN THE MOOD TO BROWSE?" END-OF-LIST CTA
     ────────────────────────────────────────────── */
  function renderEndOfListCTA() {
    var ctaContainer = document.getElementById('boost-sd-end-of-list-cta');
    if (!ctaContainer) return;

    // Check if all products are loaded (no more pages)
    var loadMoreBtn = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');
    var paginationNext = document.querySelector('.boost-sd__pagination-next:not([disabled])');
    var hasMorePages = !!(loadMoreBtn || paginationNext);

    if (hasMorePages) {
      ctaContainer.style.display = 'none';
      return;
    }

    // Show the CTA when all products have been loaded
    var productItems = document.querySelectorAll('.boost-sd__product-item');
    if (productItems.length === 0) return;

    var html = '<div class="boost-sd-end-of-list-container">';
    html += '<h1 class="new-arrivals-title">In the mood to browse?</h1>';
    html += '<div class="storybar"><ul>';
    html += '<li><a href="/collections/new-arrivals"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/NA_600x.jpg?v=1603869812" alt="New Arrivals" loading="lazy"> New Arrivals</a></li>';
    html += '<li><a href="/collections/best-sellers"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_42_600x.jpg?v=1599563007" alt="Best Sellers" loading="lazy"> Best Sellers</a></li>';
    html += '<li><a href="/collections/dresses"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_71_a5870646-a45a-455c-a19e-698a0b5ec2f2_600x.jpg?v=1605423950" alt="Dresses" loading="lazy"> Dresses</a></li>';
    html += '<li><a href="/collections/jumpsuits"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_66_cc621086-26d6-4606-b61b-db6a4d333cac_600x.jpg?v=1605424442" alt="Jumpsuits" loading="lazy"> Jumpsuits</a></li>';
    html += '<li><a href="/collections/topwear"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_45_600x.jpg?v=1599563103" alt="Tops" loading="lazy"> Tops</a></li>';
    html += '<li><a href="/collections/pants"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_70_600x.jpg?v=1605424519" alt="Pants" loading="lazy"> Pants</a></li>';
    html += '<li><a href="/collections/skirts"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_47_600x.jpg?v=1599563117" alt="Skirts" loading="lazy"> Skirts</a></li>';
    html += '<li><a href="/collections/shorts"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_48_600x.jpg?v=1599563122" alt="Shorts" loading="lazy"> Shorts</a></li>';
    html += '<li><a href="/collections/outerwear"><img class="storynav" src="//cdn.shopify.com/s/files/1/0050/3264/0601/files/Frame_44_600x.jpg?v=1599563130" alt="Outerwear" loading="lazy"> Outerwear</a></li>';
    html += '</ul></div>';
    html += '<a href="/collections/new-arrivals" class="new-arrivals Button Button--primary">Explore New Arrivals</a>';
    html += '</div>';
    ctaContainer.innerHTML = html;
    ctaContainer.style.display = 'block';
  }

  /* ──────────────────────────────────────────────
     STICKY PAGINATION ON MOBILE
     ────────────────────────────────────────────── */
  function initStickyPagination() {
    if (!isMobile()) return;

    var stickyEl = document.querySelector('.boost-sd__pagination');
    var startEl = stickyEl;
    if (!stickyEl || !startEl) return;

    var startPos = startEl.getBoundingClientRect().top + window.scrollY + 150;
    var width = stickyEl.offsetWidth;

    function setPosition() {
      var scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop < startPos) {
        stickyEl.classList.remove('boost-sd-sticky');
        stickyEl.style.position = 'initial';
        stickyEl.style.width = 'auto';
      } else {
        stickyEl.classList.add('boost-sd-sticky');
        stickyEl.style.position = 'fixed';
        stickyEl.style.top = '82px';
        stickyEl.style.width = width + 'px';
        stickyEl.style.zIndex = '10';
      }
    }

    window.addEventListener('scroll', setPosition, { passive: true });
    window.addEventListener('resize', function () {
      width = stickyEl.offsetWidth;
      setPosition();
    });
    setPosition();
  }

  /* ──────────────────────────────────────────────
     CUSTOM LOADING INDICATOR (matches original dark overlay with animated bars)
     ────────────────────────────────────────────── */
  function showLoadingIndicator() {
    var existing = document.querySelector('.boost-sd-loading-custom');
    if (existing) {
      existing.style.display = 'block';
      return;
    }
    var loader = document.createElement('div');
    loader.className = 'boost-sd-loading-custom';
    loader.innerHTML = '<div class="loadlayerwrapper"><div class="loaderLayer"><span class="loader-64"></span><p>loading</p></div></div>';
    document.body.appendChild(loader);
  }

  function hideLoadingIndicator() {
    var loader = document.querySelector('.boost-sd-loading-custom');
    if (loader) loader.style.display = 'none';
  }

  /* ──────────────────────────────────────────────
     BOOST SD (TURBO) EVENT HOOKS
     ────────────────────────────────────────────── */

  // Adjust Boost SD product grid column count to match desired responsive layout.
  // The Boost SD app applies --2-col / --3-col / --4-col classes from its dashboard
  // config. We swap the class to our desired column count after render so the app's
  // own flex-based CSS handles item sizing correctly.
  function adjustGridColumns() {
    var list = document.querySelector('.boost-sd__product-list');
    if (!list) return;
    var width = window.innerWidth;
    // Strip any existing column class
    list.classList.forEach(function (cls) {
      if (/^boost-sd__product-list-grid--\d-col$/.test(cls)) {
        list.classList.remove(cls);
      }
    });
    // Apply the correct column count for this breakpoint
    if (width < 768) {
      list.classList.add('boost-sd__product-list-grid--' + SF_CONFIG.mobileItemsPerRow + '-col');
    } else if (width < 1025) {
      list.classList.add('boost-sd__product-list-grid--' + SF_CONFIG.tabletItemsPerRow + '-col');
    } else {
      list.classList.add('boost-sd__product-list-grid--' + SF_CONFIG.desktopItemsPerRow + '-col');
    }
  }

  // Debounced resize handler for column adjustment
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(adjustGridColumns, 150);
  });

  /* ──────────────────────────────────────────────
     INJECT CUSTOMIZABLE BADGE & STICKERS INTO SSR CARDS
     ────────────────────────────────────────────── */
  function injectBadgesIntoSSRCards() {
    document.querySelectorAll('.boost-sd__product-item[data-product]').forEach(function (item) {
      // Skip if already processed
      if (item.dataset.sfBadgesInjected) return;
      item.dataset.sfBadgesInjected = 'true';

      var productData;
      try {
        productData = JSON.parse(item.getAttribute('data-product'));
      } catch (e) {
        return;
      }

      if (!productData) return;
      var tags = productData.tags || [];

      // Inject "Customizable" badge — wrap price + badge in a flex row
      var isCustomizable = tags.indexOf('Non-Customizable') === -1 && tags.indexOf('SMPLS') === -1;
      if (isCustomizable) {
        var priceEl = item.querySelector('.boost-sd__product-price');
        if (priceEl && !item.querySelector('.cust')) {
          // Wrap the price element and the customizable badge in a flex container
          var wrapper = document.createElement('div');
          wrapper.className = 'boost-sd-custom-container';
          priceEl.parentNode.insertBefore(wrapper, priceEl);
          wrapper.appendChild(priceEl);
          var custEl = document.createElement('p');
          custEl.className = 'cust modal-toggle';
          custEl.id = 'mybBtn';
          custEl.innerHTML = '\uD83E\uDEA1 Customizable';
          wrapper.appendChild(custEl);
        }
      }

      // Inject PLP sticker and size classes
      var imageWrapper = item.querySelector('.boost-sd__product-image-wrapper');
      if (imageWrapper) {
        // Size classes from tags
        var sizeClass = '';
        for (var i = 0; i < tags.length; i++) {
          if (tags[i].indexOf('Lower Size') !== -1 || tags[i].indexOf('Coord_Size') !== -1) {
            sizeClass = 'losz';
            break;
          } else if (tags[i].indexOf('Upper Size') !== -1) {
            sizeClass = 'upsz';
            break;
          }
        }
        if (sizeClass) {
          imageWrapper.classList.add(sizeClass);
        }

        // Metafield-based PLP sticker
        if (!item.querySelector('.label_icon')) {
          var metaSticker = getProductMetafield(productData, 'custom', 'plp_sticker');
          if (metaSticker) {
            var stickerDiv = document.createElement('div');
            stickerDiv.className = 'label_icon label_icon-mob ' + slugify(metaSticker);
            stickerDiv.textContent = metaSticker;
            imageWrapper.appendChild(stickerDiv);
          }
        }
      }
    });
  }
  // Listen for Boost SD product list rendered events
  function onProductsRendered(event) {
    adjustGridColumns();

    // Inject badges into SSR-rendered cards
    injectBadgesIntoSSRCards();

    initSwatchInteractions();
    initCustomizableToggle();
    hideLoadingIndicator();
    styleSwatchExpanders();

    // Always inject product count — Boost re-renders destroy previous injections
    var data = event && event.detail ? event.detail : null;
    updateProductCount(data);
    styleLoadMoreButton();

    // Retry with increasing delays — Boost may still be populating the DOM
    [300, 800, 2000].forEach(function(delay) {
      setTimeout(function() {
        // Re-inject badges in case SSR cards appeared after initial pass
        injectBadgesIntoSSRCards();
        if (!document.getElementById('sf-custom-product-count') ||
            !document.getElementById('sf-custom-page-progress') ||
            !document.getElementById('sf-desktop-product-count')) {
          updateProductCount(null);
        }
        styleLoadMoreButton();
        styleSwatchExpanders();
      }, delay);
    });

    updateMobileClearButton();

    // Delay end-of-list CTA check to allow DOM to settle
    setTimeout(renderEndOfListCTA, 300);

    // Re-initialize sticky pagination on mobile
    if (isMobile()) {
      setTimeout(initStickyPagination, 500);
    }

    // Re-initialize review badges if available
    if (typeof StampedFn !== 'undefined' && typeof StampedFn.loadBadges === 'function') {
      StampedFn.loadBadges();
    }
  }

  /* ──────────────────────────────────────────────
     COLLECTION DESCRIPTION TOGGLE (tap to expand/collapse)
     ────────────────────────────────────────────── */
  function initDescriptionToggle() {
    if (window._descToggleBound) return;
    window._descToggleBound = true;
    document.addEventListener('click', function (e) {
      var desc = e.target.closest('.boost-sd__header-description');
      if (desc) {
        desc.classList.toggle('expanded');
      }
    });
  }

  /* ──────────────────────────────────────────────
     DELIVERY DATE INJECTION INTO BOOST HEADER
     ────────────────────────────────────────────── */
  function injectDeliveryDate() {
    if (document.getElementById('sf-collection-delivery-date')) return;
    var header = document.querySelector('.boost-sd__collection-header');
    if (!header) return;

    var el = document.createElement('div');
    el.id = 'sf-collection-delivery-date';
    el.innerHTML = 'Order today for Delivery by ~<span class="sf-delivery-val cartETD">calculating...</span>';
    header.appendChild(el);

    // Calculate delivery date (12 business days, skip weekends + holidays)
    var days = 12;
    var holidays = ['01-01','01-26','03-29','04-14','05-01','08-15','10-02','11-01','12-25'];
    var date = new Date();
    var added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      var day = date.getDay();
      if (day === 0 || day === 6) continue;
      var mmdd = ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
      if (holidays.indexOf(mmdd) !== -1) continue;
      added++;
    }
    var formatted = date.getDate() + ' ' + date.toLocaleString('default', { month: 'short' });
    document.querySelectorAll('.cartETD').forEach(function(t) { t.textContent = formatted; });
    window.__deliveryDateFormatted = formatted;
  }

  // --- PLP Banner injection ---
  function injectPLPBanner() {
    if (document.getElementById('sf-plp-banner')) return;
    var header = document.querySelector('.boost-sd__collection-header');
    if (!header) return;

    var banner = document.createElement('div');
    banner.id = 'sf-plp-banner';
    banner.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0050/3264/0601/files/Icons_v6.2_1.jpg?v=1752229475" alt="SeamsFriendly — Handcrafted, 100% Cotton, Free Shipping, Easy Returns" loading="lazy" class="sf-plp-banner__img">';
    header.parentNode.insertBefore(banner, header.nextSibling);
  }

  // --- Combined header injection (delivery date + PLP banner) ---
  function injectHeaderElements() {
    initDescriptionToggle();
    injectDeliveryDate();
    injectPLPBanner();
  }

  // MutationObserver: watch for Boost re-rendering the collection header.
  // When Boost re-renders, our injected delivery date and PLP banner get destroyed.
  // This observer detects that and re-injects them.
  (function watchHeaderReRenders() {
    var headerObserver = new MutationObserver(function(mutations) {
      // If delivery date or banner got removed, re-inject
      if (!document.getElementById('sf-collection-delivery-date') ||
          !document.getElementById('sf-plp-banner')) {
        var header = document.querySelector('.boost-sd__collection-header');
        if (header) {
          injectHeaderElements();
        }
      }

      // If Load More button exists but isn't styled, style it and re-inject progress
      var loadMoreBtn = document.querySelector('.boost-sd__pagination-button--load-more, .boost-sd__load-more-btn');
      if (loadMoreBtn && !loadMoreBtn.dataset.sfStyled) {
        styleLoadMoreButton();
        updateProductCount(null);
      }
      // Re-inject customizable badges into any new SSR cards
      var unprocessedCards = document.querySelectorAll('.boost-sd__product-item[data-product]:not([data-sf-badges-injected])');
      if (unprocessedCards.length > 0) {
        injectBadgesIntoSSRCards();
      }
    });

    function startObserving() {
      // Observe the layout container that wraps the collection header
      var container = document.querySelector('.boost-sd-layout') ||
                      document.querySelector('[class*="boost-sd"]');
      if (container) {
        headerObserver.observe(container, { childList: true, subtree: true });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving);
    } else {
      startObserving();
    }
  })();

  // Boost SD Turbo event listeners
  document.addEventListener('boost-sd:product-list-rendered', onProductsRendered);
  // Initialize description toggle when collection header renders
  document.addEventListener('boost-sd:collection-header-rendered', function() {
    injectHeaderElements();
  });
  // Fallback: also init on DOMContentLoaded for SSR-rendered content
  function initSSRFallback() {
    repositionToolbar();
    injectHeaderElements();
    // SSR cards are already in the DOM — inject badges
    if (document.querySelector('.boost-sd__product-item[data-product]')) {
      injectBadgesIntoSSRCards();
      initSwatchInteractions();
      initCustomizableToggle();
    }
    // Try to inject product count for SSR-rendered pages
    updateProductCount(null);
    styleLoadMoreButton();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSSRFallback);
  } else {
    initSSRFallback();
  }

  document.addEventListener('boost-sd:filter-rendered', function () {
    // Add overlay click handler for mobile filter drawer
    var overlay = document.querySelector('.boost-sd__filter-overlay');
    if (overlay) {
      overlay.addEventListener('click', function () {
        var closeBtn = document.querySelector('.boost-sd__filter-close-btn');
        if (closeBtn) closeBtn.click();
      });
    }

    // Override Boost's inline transform so filter drawer slides from the right
    var filterColumn = document.getElementById('boost-sd-filter-tree-column');
    if (filterColumn && window.innerWidth < 750) {
      // Remove Boost's default left positioning
      filterColumn.style.left = 'auto';
      filterColumn.style.right = '0';
      // If drawer is closed, push it off-screen to the right
      if (!filterColumn.classList.contains('boost-sd__filter-tree-vertical--open')) {
        filterColumn.style.transform = 'translateX(100%)';
      }
    }

    // Hide "relevance" sort option (matches original)
    document.querySelectorAll('[data-value="relevance"]').forEach(function (el) {
      if (el.closest('[class*="boost-sd__sort"]') || el.closest('[class*="boost-sd__filter-top-sorting"]')) {
        el.style.display = 'none';
      }
    });

    // Style Load More button via inline styles (Boost CSS overrides theme CSS)
    styleLoadMoreButton();
  });

  // Ensure filter drawer always opens from the right on mobile
  (function enforceRightDrawer() {
    function fixDrawerPosition() {
      if (window.innerWidth >= 750) return;
      var col = document.getElementById('boost-sd-filter-tree-column');
      if (!col) return;
      col.style.left = 'auto';
      col.style.right = '0';
    }

    // Observe attribute/class changes on filter column to re-apply position
    var drawerObserver = new MutationObserver(function() {
      fixDrawerPosition();
    });

    function attachDrawerObserver() {
      var col = document.getElementById('boost-sd-filter-tree-column');
      if (col) {
        fixDrawerPosition();
        drawerObserver.observe(col, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    }

    // Attach on filter render and DOM ready
    document.addEventListener('boost-sd:filter-rendered', attachDrawerObserver);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachDrawerObserver);
    } else {
      attachDrawerObserver();
    }
  })();

  // Scroll to top on filter/sort actions (matches original behavior)
  document.addEventListener('boost-sd:filter-applied', function () {
    scrollToTop();
    showLoadingIndicator();
  });
  document.addEventListener('boost-sd:sort-applied', function () {
    scrollToTop();
    showLoadingIndicator();
  });

  // MutationObserver fallback for when events aren't fired
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.addedNodes.length > 0) {
        var hasNewProducts = false;
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList &&
              (node.classList.contains('boost-sd__product-item') ||
               node.classList.contains('sf-product-card'))) {
            hasNewProducts = true;
          }
        });
        if (hasNewProducts) {
          setTimeout(onProductsRendered, 100);
        }
      }
    });
  });

  // Start observing when DOM is ready
  function startObserving() {
    var productContainer = document.querySelector('.boost-sd__product-list');
    if (productContainer) {
      observer.observe(productContainer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  /* ──────────────────────────────────────────────
     INSTANT SEARCH CUSTOMIZATIONS
     ────────────────────────────────────────────── */

  // Handle Boost SD instant search rendered event
  document.addEventListener('boost-sd:search-rendered', function () {
    // Mobile: reorder suggestion blocks to show products first
    if (isMobile()) {
      var suggestionEl = document.querySelector('.boost-sd__search-suggestion');
      if (suggestionEl) {
        var productBlock = suggestionEl.querySelector('[data-group="products"]');
        if (productBlock && productBlock !== suggestionEl.firstElementChild) {
          suggestionEl.prepend(productBlock);
        }
      }
    }
  });

  // Fix search box enter key submission
  document.addEventListener('keypress', function (e) {
    if (e.keyCode === 13) {
      var target = e.target;
      if (target && target.classList && target.classList.contains('boost-sd__search-input')) {
        var form = target.closest('form');
        if (form) form.submit();
      }
    }
  });

  /* ──────────────────────────────────────────────
     SCROLL TO TOP ON PAGE LOAD
     ────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    // Only on collection/search pages
    if (document.body.dataset.template &&
        (document.body.dataset.template.indexOf('collection') !== -1 ||
         document.body.dataset.template.indexOf('search') !== -1)) {
      window.scrollTo(0, 0);
    }
  });

  /* ──────────────────────────────────────────────
     EXPOSE GLOBAL API
     ────────────────────────────────────────────── */
  window.sfBoostCustom = {
    buildProductCard: buildProductCard,
    config: SF_CONFIG,
    formatMoney: formatMoney,
    onProductsRendered: onProductsRendered,
    showLoading: showLoadingIndicator,
    hideLoading: hideLoadingIndicator,
    scrollToTop: scrollToTop
  };

  /* ──────────────────────────────────────────────
     BOOST SD APP CONFIG CUSTOMIZATION
     ────────────────────────────────────────────── */
  // Register via boostSDCustom global (primary Turbo pattern)
  window.boostSDCustom = window.boostSDCustom || {};
  window.boostSDCustom.productCard = buildProductCard;
  window.boostSDCustom.afterRender = onProductsRendered;

  // Also register via boostSDAppConfig if available (or set up for later)
  function registerAppConfig() {
    if (typeof window.boostSDAppConfig !== 'undefined') {
      window.boostSDAppConfig.customization = window.boostSDAppConfig.customization || {};
      window.boostSDAppConfig.customization.productCardTemplate = buildProductCard;
    }
  }
  registerAppConfig();
  // Retry after DOM ready in case Turbo loads later
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerAppConfig);
  }

})();
